/**
 * GraphCanvas.tsx — the React Flow viewport.
 *
 * Converts the engine's Graph (nodes/edges) + the current tick into React
 * Flow's node/edge shapes, with our custom ActorNode + FlowEdge types. The
 * conversion is memoised on `step` so a tick doesn't churn React Flow's
 * internal store — only the per-tick bits (stock, flow, annotations) flow
 * through `data`.
 *
 * Selection (click a node/edge) surfaces through `onSelectNode` / `onSelectEdge`
 * so the App can open the nerd-mode modals. Auto-layout uses the node's
 * authored `position` if present; React Flow's controls (zoom, pan, fit) are
 * enabled so a busy graph is navigable.
 *
 * #1 — Edges carry arrowheads driven by the current tick's flow sign: a
 * positive flow puts a marker at the target, a negative flow at the source.
 * `showArrows` (top-bar toggle) hides them entirely. Signal edges never get
 * arrows — they carry state, not stock.
 *
 * #2 — Drag: live positions flow through `applyNodeChanges`; the bundle write
 * fires only on drop. A `draggingRef` gates the `useSim` bundle→nodes re-fit
 * so a drag isn't interrupted by a tick re-publish.
 *
 * #3 — Right-click on pane / node / edge opens the context menu via the
 * `onContextPane` / `onContextNode` / `onContextEdge` props.
 */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import {
  Background,
  BackgroundVariant,
  ConnectionMode,
  Controls,
  MiniMap,
  ReactFlow,
  ReactFlowProvider,
  MarkerType,
  useNodesState,
  useReactFlow,
  type Connection,
  type Edge,
  type EdgeMarker,
  type Node,
  type NodeChange,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import type {
  Graph,
  GraphNode,
  GraphEdge,
  Bend,
  NodeGroup,
  TickPayload,
} from '../engine/types';
import type { OverlayState } from '../overlays/annotations';
import { ActorNode, type ActorNodeData } from './ActorNode';
import { FlowEdge, edgeDerivation, edgeDerivationTitle, type FlowEdgeData } from './FlowEdge';
import { GroupRect, type GroupNodeData } from './GroupRect';
import { flowKindColor, groupColor } from './palette';
import { autoRouteEdge } from './routing';
import { memberIdsOf } from './groups';
import { authoredPositions, syncPositionFor } from './nodeSync';

// Custom types registered with React Flow so it renders our components.
const nodeTypes = { actor: ActorNode, gate: ActorNode, group: GroupRect };
const edgeTypes = { flow: FlowEdge };

interface GraphCanvasProps {
  graph: Graph;
  tick: TickPayload;
  /** True when ticks come from an external model subprocess (api/relay) —
   *  edge-label tooltips say so, since the authored weights then describe
   *  the routing the in-browser engine would apply, not the live numbers. */
  flowsFromModel?: boolean;
  /** Node ids a bundle-level workflowTrigger sources (the trigger
   *  contract) — the ⚡ badge. Computed by the owner from the bundle. */
  workflowTriggerNodes?: ReadonlySet<string>;
  /** Node ids whose triggers are ALL disarmed — the ⚡ renders dimmed. */
  workflowTriggerDisarmed?: ReadonlySet<string>;
  /** Node ids whose trigger fired/failed in the last seconds — the ⚡
   *  pulses (live fire feedback from the relay path's broadcasts). */
  workflowTriggerFlash?: ReadonlySet<string>;
  overlays: OverlayState;
  /** When false, overlays are suppressed — the "show model projections"
   *  toggle (regular users: on; nerd mode: inspect raw payload). */
  showOverlays: boolean;
  /** When true, arrowheads render on edges (driven by flow direction). */
  showArrows: boolean;
  /** When true, the editor is enabled: an "add node" button, draggable nodes
   *  (positions written back), and edge creation by dragging between handles. */
  editable: boolean;
  /** Double-click on the pane zooms (React Flow's default). The REPLAY
   *  canvas passes false — double-click opens node/edge modals there, and
   *  d3-zoom's pane listener fires on native bubble BEFORE React's node
   *  handler can stopPropagation, so the canvas would zoom under the modal. */
  zoomOnDoubleClick?: boolean;
  selectedNodeId: string | null;
  selectedEdgeId: string | null;
  onSelectNode: (id: string | null) => void;
  onSelectEdge: (id: string | null) => void;
  /** Double-click a node → open its modal (distinct from selection). */
  onOpenNode?: (id: string) => void;
  /** Double-click an edge label → open its modal (already used by label click). */
  onOpenEdge?: (id: string) => void;
  /** ─── Spatial node groups (rectangles behind the graph) ───
   *  Membership is derived from positions; the canvas passes the resolved
   *  member count down to each GroupRect for its header badge. */
  nodeGroups?: NodeGroup[];
  selectedGroupId?: string | null;
  onSelectGroup?: (id: string | null) => void;
  /** Double-click a group header → open its modal. */
  onOpenGroup?: (id: string) => void;
  onUpdateGroup?: (group: NodeGroup) => void;
  onContextGroup?: (id: string, clientPos: { x: number; y: number }) => void;
  onUpdateNode?: (node: GraphNode) => void;
  onConnect?: (edge: GraphEdge) => void;
  /** Re-anchor an edge's source/target (used by RF's edge-reconnect drag). */
  onReconnectEdge?: (
    edgeId: string,
    next: { source?: string; target?: string; sourceHandle?: string; targetHandle?: string },
  ) => void;
  /** Add a bend point. `idx` is the insertion position in the bend chain
   *  (computed from where on the curve the user clicked, so the new bend
   *  lands between its actual neighbours — not always at the end). */
  onAddBend?: (edgeId: string, x: number, y: number, idx: number, fallbackBends?: Bend[]) => void;
  /** Move an existing bend (drag). */
  onMoveBend?: (edgeId: string, idx: number, x: number, y: number, fallbackBends?: Bend[]) => void;
  /** Promote an auto-routed edge to explicit when the user interacts with it */
  onPromoteEdge?: (edgeId: string, sourceHandle: string | undefined, targetHandle: string | undefined, bends: Bend[]) => void;
  /** Right-click a bend → context menu (sharp/smooth/default/delete). */
  onBendContext?: (edgeId: string, idx: number, clientX: number, clientY: number) => void;
  /** Drag the weight label → update its position along the curve (t ∈ [0,1]). */
  onMoveLabel?: (edgeId: string, t: number) => void;
  /** Context-menu callbacks (#3). Pane coords are canvas-space (converted
   *  here via screenToFlowPosition); node/edge coords are client-space. */
  onContextPane?: (canvasPos: { x: number; y: number }, clientPos: { x: number; y: number }) => void;
  onContextNode?: (id: string, clientPos: { x: number; y: number }) => void;
  onContextEdge?: (id: string, clientPos: { x: number; y: number }) => void;
  /** #4 — Groups panel state. Edges in a hidden group are removed from the
   *  rendered edge list; edges in a highlighted group (or in the hover-preview
   *  group) get a glow ring; the rest dim when any highlight is active. */
  hiddenGroups?: Set<string>;
  highlightedGroups?: Set<string>;
  hoverGroup?: string | null;
  /** #5 — When true, render a square-grid background instead of the dotted
   *  default. Toggled from the StatusBar. */
  showGrid?: boolean;
  /** When true, render the React Flow MiniMap (bottom-right). Toggled from
   *  the StatusBar; defaults off. */
  showMinimap?: boolean;
  /** Whether the sim is currently ticking. When false, edge dash animation
   *  stops (a paused edge should look still, not keep flowing). */
  running?: boolean;
}

/** Imperative surface for the App's keyboard bindings. React Flow's selection
 *  state (shift-click / marquee multi-select) lives in RF's own store — the
 *  App can't read it from props, so the canvas exposes it here, UNIONED at the
 *  App layer with the App's single-selection ids (`selectedNode/selectedEdge`
 *  drive the sidebar + modals; RF selection drives copy/paste/delete keys). */
export interface GraphCanvasHandle {
  /** RF-selected actor node ids (group rects are never selectable). */
  getSelectedNodeIds(): string[];
  /** RF-selected edge ids. */
  getSelectedEdgeIds(): string[];
  /** Mark every actor node selected (Ctrl/Cmd+A). */
  selectAll(): void;
  /** Drop RF's multi-selection flags (Escape). */
  clearSelection(): void;
  /** Viewport controls (Ctrl/Cmd+0 etc.) — RF Controls parity for the keys. */
  fitView(): void;
  zoomIn(): void;
  zoomOut(): void;
}

/**
 * Own ReactFlowProvider + explicit id="graph". Every canvas MUST own its
 * store: the app keeps this canvas mounted (display:none) while the
 * Workflows tab is open, and two <ReactFlow> components sharing one provider
 * corrupt each other's node stores. The explicit `id` is equally load-bearing
 * for HIDDEN canvases: RF derives every DOM id from rfId (Background SVG
 * pattern ids, a11y description nodes), and rfId defaults to '1' for ALL
 * instances. The workflow shell sits BEFORE this canvas in the DOM, so with
 * colliding pattern ids this canvas's fill url(#pattern-1) would resolve
 * into the hidden workflow's pattern and the grid would silently vanish.
 */
export const GraphCanvas = forwardRef<GraphCanvasHandle, GraphCanvasProps>(
  function GraphCanvas(props, ref) {
    return (
      <ReactFlowProvider>
        <GraphCanvasInner {...props} ref={ref} />
      </ReactFlowProvider>
    );
  },
);

const GraphCanvasInner = forwardRef<GraphCanvasHandle, GraphCanvasProps>(
  function GraphCanvasInner({
    graph,
    tick,
    flowsFromModel,
    workflowTriggerNodes,
    workflowTriggerDisarmed,
    workflowTriggerFlash,
    overlays,
    showOverlays,
    showArrows,
    editable,
    zoomOnDoubleClick = true,
    selectedNodeId,
    selectedEdgeId,
    onSelectNode,
    onSelectEdge,
    onOpenNode,
    onOpenEdge,
    nodeGroups,
    selectedGroupId,
    onSelectGroup,
    onOpenGroup,
    onUpdateGroup,
    onContextGroup,
    onUpdateNode,
    onConnect,
    onReconnectEdge,
    onAddBend,
    onMoveBend,
    onPromoteEdge,
    onBendContext,
    onMoveLabel,
    onContextPane,
    onContextNode,
    onContextEdge,
    hiddenGroups,
    highlightedGroups,
    hoverGroup,
    showGrid = false,
    showMinimap = false,
    running = false,
  }, ref): JSX.Element {
  const cues = overlays.nodeCues;
  const edgeCues = overlays.edgeCues;
  const reactFlow = useReactFlow();
  // Drag gate (#2): when true, the canvas ignores bundle→nodes re-fits so a
  // drag in progress isn't snapped back by an interleaving tick publish.
  const draggingRef = useRef(false);
  // Last-known measured dimensions per node id. The `nodes` memo below rebuilds
  // on every tick (its `tick` dep changes each step), producing fresh node
  // objects WITHOUT a `measured` field. In controlled mode, passing RF an
  // unmeasured node resets its internal measurement state — RF re-marks the
  // node `visibility: hidden` until the ResizeObserver fires again. At high
  // tick rates the next rebuild lands before measurement completes, so nodes
  // stay permanently hidden (the "canvas clears after a while" / "nodes
  // disappear during playback" bug). Carrying forward the last `measured`
  // dimensions keeps RF from resetting on every tick.
  const measuredRef = useRef<Record<string, { width: number; height: number }>>({});

  const nodes: Node<ActorNodeData | GroupNodeData>[] = useMemo(() => {
    // Group rectangles render behind actor nodes. RF orders nodes by array
    // order when zIndex is equal, so groups go first AND get an explicit low
    // zIndex to guarantee they stay below selected/dragged actors (RF lifts
    // those to zIndex 1000). selectable:false keeps RF from promoting a group
    // on click.
    const groupNodes: Node<GroupNodeData>[] = (nodeGroups ?? []).map((g) => ({
      id: g.id,
      type: 'group',
      position: { x: g.rect.x, y: g.rect.y },
      // RF applies the position to the wrapper; the inner GroupRect sizes
      // itself from rect.w/h.
      data: {
        group: g,
        editable,
        selected: selectedGroupId === g.id,
        memberCount: memberIdsOf(g, graph.nodes).size,
        onSelect: onSelectGroup,
        onOpen: onOpenGroup,
        onContext: (id: string, x: number, y: number) => onContextGroup?.(id, { x, y }),
        onResize: (_id: string, rect: NodeGroup['rect']) =>
          onUpdateGroup?.({ ...g, rect }),
      },
      selectable: false,
      draggable: editable,
      // zIndex:-1 keeps the group behind actor nodes (RF lifts selected/
      // dragged nodes to 1000). We deliberately do NOT set width/height on
      // the RF node object — RF would apply them to the wrapper as inline
      // styles and they'd conflict with the px size on .group-rect; the
      // wrapper shrink-wraps the .group-rect instead (see .react-flow__node-group
      // in style.css).
      zIndex: -1,
    }));
    const actorNodes: Node<ActorNodeData>[] = graph.nodes.map((n) => {
      const v = tick.nodeValues[n.id];
      const cue = cues[n.id];
      const isBn = showOverlays && cue?.bottleneck === true;
      const isAnom = showOverlays && cue?.anomalous === true;
      // Cluster recolour wins over the default group colour when set.
      const ring = showOverlays && cue?.clusterColor ? cue.clusterColor : n.group ? groupColor(n.group) : undefined;
      const m = measuredRef.current[n.id];
      return {
        id: n.id,
        type: n.kind === 'gate' ? 'gate' : 'actor',
        position: n.position ?? { x: 0, y: 0 },
        // Carry forward the last-measured dimensions. Without this, RF sees a
        // fresh unmeasured node object on every rebuild (the memo re-runs each
        // tick) and resets `visibility: hidden` until the ResizeObserver
        // re-fires — at high tick rates measurement never completes and nodes
        // stay permanently hidden (the long-lived "canvas clears during
        // playback" bug). Carrying measured forward keeps RF stable.
        measured: m ?? undefined,
        data: {
          label: n.label,
          icon: n.icon,
          group: n.group,
          stock: v?.stock,
          inflow: v?.inflow,
          outflow: v?.outflow,
          bottleneck: isBn,
          anomalous: isAnom,
          hasAgent: !!n.agentConfig,
          hasWorkflowTrigger: workflowTriggerNodes?.has(n.id) ?? false,
          triggerDisarmed: workflowTriggerDisarmed?.has(n.id) ?? false,
          triggerFlash: workflowTriggerFlash?.has(n.id) ?? false,
          onSelect: onSelectNode,
          onOpen: onOpenNode,
          onContext: (id: string, x: number, y: number) => onContextNode?.(id, { x, y }),
          groupColor: ring,
          displayValue: n.displayValue,
          // The resolved `state:<key>` scalar (NOT the whole state record —
          // a fresh object per tick would break the patch's identity check).
          stateValue: n.displayValue?.startsWith('state:')
            ? v?.state?.[n.displayValue.slice('state:'.length)]
            : undefined,
        },
        selected: selectedNodeId === n.id,
      };
    });
    return [...groupNodes, ...actorNodes];
    // draggingRef is intentionally excluded — we don't want a drag to re-fit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graph, tick, cues, showOverlays, selectedNodeId, onSelectNode, onOpenNode, nodeGroups, selectedGroupId, onSelectGroup, onOpenGroup, onContextGroup, onUpdateGroup, editable, workflowTriggerNodes, workflowTriggerDisarmed, workflowTriggerFlash]);

  // #2 — React Flow owns the live node store. This is the fix for the
  // long-lived "canvas clears during playback" bug: previously we passed a
  // freshly-rebuilt `nodes` array (via the memo above) to RF's `nodes` prop on
  // every tick. In controlled mode RF syncs its internal store to the incoming
  // prop — fresh node objects WITHOUT `measured` reset RF's measurement state
  // and every node goes `visibility: hidden` until the ResizeObserver re-fires.
  // At high tick rates the next rebuild lands before measurement completes, so
  // nodes stay permanently hidden. With `useNodesState`, RF owns the nodes
  // (preserving `measured` across renders); we only sync STRUCTURE changes
  // (add/remove/rename/reposition) in from the memo, and push the per-tick
  // VALUES (stock/inflow/outflow/cues) through `updateNodeData` which patches
  // RF's store in place without touching node identity.
  const [rfNodes, setRfNodes, onRfNodesChange] = useNodesState<Node<ActorNodeData | GroupNodeData>>(nodes);

  // The keyboard-binding surface (see GraphCanvasHandle). Reads/writes RF's
  // own selection store via the same change pipeline the canvas already uses,
  // so select-changes respect `selectable` flags and stay consistent with
  // click-driven selection.
  useImperativeHandle(
    ref,
    () => ({
      getSelectedNodeIds: () =>
        reactFlow.getNodes().filter((n) => n.selected && n.type !== 'group').map((n) => n.id),
      getSelectedEdgeIds: () => reactFlow.getEdges().filter((e) => e.selected).map((e) => e.id),
      selectAll: () => {
        onRfNodesChange(
          reactFlow
            .getNodes()
            .filter((n) => n.type !== 'group')
            .map((n) => ({ id: n.id, type: 'select' as const, selected: true })),
        );
      },
      clearSelection: () => {
        onRfNodesChange(
          reactFlow.getNodes().map((n) => ({ id: n.id, type: 'select' as const, selected: false })),
        );
      },
      fitView: () => {
        void reactFlow.fitView({ padding: 0.2, duration: 200 });
      },
      zoomIn: () => reactFlow.zoomIn({ duration: 200 }),
      zoomOut: () => reactFlow.zoomOut({ duration: 200 }),
    }),
    [reactFlow, onRfNodesChange],
  );
  // Track the last graph reference we synced from, so we only re-sync when the
  // structure actually changes (not every tick). `lastGroupsRef` extends the
  // structure key to group edits (rect moves live on bundle.nodeGroups, not
  // graph).
  const lastGraphRef = useRef<Graph | null>(null);
  const lastGroupsRef = useRef<NodeGroup[] | undefined>(undefined);
  const draggingIdRef = useRef<string | null>(null);

  const edges: Edge<FlowEdgeData>[] = useMemo(() => {
    // #4 — Group visibility + highlight. A hidden group's edges are dropped
    // entirely from the rendered list (RF removes them from the canvas).
    // When any group is highlighted OR a hover-preview is active, the
    // highlighted edges get a glow (`glow: true`) and the rest dim (`dim: true`).
    const anyHighlight =
      (highlightedGroups && highlightedGroups.size > 0) || !!hoverGroup;

    // For auto-routing, we need the current position of all nodes, including
    // during drag. `rfNodes` is RF's owned store — it carries the live (dragged)
    // positions, so edges re-route as the user drags.
    const rfPosByid = new Map(rfNodes.map(n => [n.id, n.position]));
    const activeNodes = graph.nodes.map(n => {
      const pos = rfPosByid.get(n.id);
      if (pos) return { ...n, position: pos };
      return n;
    });

    // Group edges by (source, target) to distribute multiple edges over snap points
    const edgeCounts: Record<string, GraphEdge[]> = {};
    for (const e of graph.edges) {
      const key = `${e.source}->${e.target}`;
      if (!edgeCounts[key]) edgeCounts[key] = [];
      edgeCounts[key].push(e);
    }

    return graph.edges
      .filter((e) => {
        const g = e.group;
        // An edge with no group is never group-hidden.
        if (!g) return true;
        return !(hiddenGroups && hiddenGroups.has(g));
      })
      .map((e) => {
        const flow = tick.edgeFlows[e.id] ?? 0;
        const color = flowKindColor(e.flowKind, e.customKind, e.customColor);
        const isSignal = e.flowKind === 'signal';
        // #1 — Arrows ALWAYS show on authored edges (the arrow reflects the
        // authored source→target direction). Signal edges stay arrow-less.
        const marker: EdgeMarker = { type: MarkerType.ArrowClosed, color, width: 10, height: 10 };
        const markerEnd = showArrows && !isSignal ? marker : undefined;
        const g = e.group;
        const lit = anyHighlight && (!!g && (highlightedGroups?.has(g) || hoverGroup === g));
        const isAuto = !e.sourceHandle && !e.targetHandle && (!e.bends || e.bends.length === 0);

        let sourceHandle = e.sourceHandle;
        let targetHandle = e.targetHandle;
        let bends = e.bends;

        if (isAuto) {
          const sourceNode = activeNodes.find(n => n.id === e.source);
          const targetNode = activeNodes.find(n => n.id === e.target);
          if (sourceNode && targetNode) {
            const groupEdges = edgeCounts[`${e.source}->${e.target}`];
            const edgeIndex = groupEdges.indexOf(e);
            const routed = autoRouteEdge(sourceNode, targetNode, activeNodes, edgeIndex, groupEdges.length);
            sourceHandle = routed.sourceHandle;
            targetHandle = routed.targetHandle;
            bends = routed.bends;
          }
        }

        return {
          id: e.id,
          type: 'flow',
          source: e.source,
          target: e.target,
          sourceHandle,
          targetHandle,
          // The edge body stays hit-detectable (so double-click adds a bend +
          // reconnect works + right-click opens the context menu). The MODAL
          // is gated at the App level: only the weight-label click opens it,
          // because onSelectEdge fires only from the label button.
          // interactionWidth keeps the invisible hit area generous (default 20).
          interactionWidth: 24,
          // Reconnect: both endpoints are draggable to re-anchor the edge to a
          // different node side. The actual update flows through onReconnect.
          reconnectable: editable ? true : false,
          markerEnd,
          selected: selectedEdgeId === e.id,
          data: {
            flowKind: e.flowKind,
            customKind: e.customKind,
            customColor: e.customColor,
            weight: e.weight,
            flow,
            derivation: edgeDerivation(e),
            derivationTitle: edgeDerivationTitle(e) + (flowsFromModel ? ' (values posted by the running model)' : ''),
            flowsFromModel,
            slowdown: showOverlays && edgeCues[e.id]?.slowdown === true,
            running,
            glow: lit,
            dim: anyHighlight && !lit,
            editable,
            bends,
            isAuto,
            computedSourceHandle: sourceHandle,
            computedTargetHandle: targetHandle,
            labelT: e.labelT,
            onMoveBend,
            onPromoteEdge,
            onBendContext,
            onMoveLabel,
            onSelect: onSelectEdge,
            onOpen: onOpenEdge,
            onContext: (id: string, x: number, y: number) => onContextEdge?.(id, { x, y }),
          },
        };
      });
  }, [graph, tick, flowsFromModel, rfNodes, edgeCues, showOverlays, showArrows, selectedEdgeId, onSelectEdge, onOpenEdge, onMoveBend, onPromoteEdge, onBendContext, onMoveLabel, onContextEdge, hiddenGroups, highlightedGroups, hoverGroup, running, editable]);

  const onNodesChange = useCallback(
    (changes: NodeChange<Node<ActorNodeData | GroupNodeData>>[]) => {
      // Let RF apply the changes to its own store first (this is what preserves
      // `measured` across renders — the fix for the "canvas clears during
      // playback" bug). Then intercept position-end events to persist the
      // final position back to the bundle.
      onRfNodesChange(changes);
      if (!editable) return;
      for (const c of changes) {
        if (c.type === 'position') {
          if (c.dragging === true) {
            draggingRef.current = true;
            draggingIdRef.current = c.id;
          } else if (c.dragging === false) {
            draggingRef.current = false;
            draggingIdRef.current = null;
            // Persist the final position to the bundle. A group node (RF type
            // 'group') is NOT a graph node — its move re-roots the group rect.
            const groupNode = (nodeGroups ?? []).find((g) => g.id === c.id);
            if (groupNode && c.position) {
              onUpdateGroup?.({
                ...groupNode,
                rect: { ...groupNode.rect, x: c.position.x, y: c.position.y },
              });
            } else {
              const orig = graph.nodes.find((n) => n.id === c.id);
              if (orig && c.position) onUpdateNode?.({ ...orig, position: c.position });
            }
          }
        }
      }
    },
    [editable, onRfNodesChange, graph.nodes, nodeGroups, onUpdateNode, onUpdateGroup],
  );

  // Keep a ref to the latest derived `nodes` so the structure-sync effect
  // below can read it WITHOUT depending on it. The `nodes` memo rebuilds on
  // every render (its deps include unstable parent callbacks), so depending
  // on `nodes` directly would churn RF's store + reset `measured` on every
  // render — the canvas-clearing bug. Reading from a ref breaks that cycle.
  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;

  // Sync STRUCTURE changes (add/remove/rename/reposition a node, or a group
  // change) from the derived `nodes` memo into RF's owned store — but ONLY
  // when the graph reference actually changes. This is deliberately NOT keyed
  // on `tick` or `nodes` identity: per-tick value updates flow through the
  // `updateNodeData` effect below, leaving node identity + `measured` intact.
  // When we do sync, we preserve `measured` from the existing RF nodes (the
  // visibility fix) and resolve `position` through `syncPositionFor`: a node
  // WITH an authored position renders AT it whenever the sync sees a
  // divergence (a drag commit, an undo/redo landing); a node without one
  // keeps RF's live position (auto-layout must not snap back to the memo's
  // {0,0} fallback). Group edits (rect moves) are part of the structure key:
  // they live on `bundle.nodeGroups`, not `graph`, so watching `graph` alone
  // would never sync them.
  useEffect(() => {
    const structureChanged =
      lastGraphRef.current !== graph || lastGroupsRef.current !== nodeGroups;
    lastGraphRef.current = graph;
    lastGroupsRef.current = nodeGroups;
    if (!structureChanged || draggingRef.current) return; // don't clobber a drag in progress
    const latest = nodesRef.current;
    const authored = authoredPositions(graph, nodeGroups);
    setRfNodes((prev) => {
      const prevById = new Map(prev.map((n) => [n.id, n]));
      return latest.map((n) => {
        const existing = prevById.get(n.id);
        return {
          ...n,
          measured: existing?.measured ?? n.measured,
          position: syncPositionFor(n.id, n.position, existing?.position, authored),
        };
      });
    });
  }, [graph, nodeGroups, setRfNodes]);

  // Push tick-derived values into OUR rfNodes state. Do NOT use
  // `reactFlow.updateNodeData` here: in controlled mode it never writes the
  // store — it diffs its patch against RF's INTERNAL nodes and fires
  // `onNodesChange` with `{type:'replace', item}` changes carrying the
  // STORE's node objects. Those get applied after the structure sync and
  // clobber it — the canvas re-adopts RF's stale positions, which is exactly
  // how "undo restores the bundle but the node doesn't move back" happened.
  // Patching our own state keeps positions + `measured` (the `{...n}` spread)
  // and is identity-stable when values don't change, so RF doesn't re-adopt
  // (or reset `visibility: hidden`) on unchanged ticks.
  useEffect(() => {
    setRfNodes((prev) => {
      let changed = false;
      const next = prev.map((n) => {
        if (n.type === 'group') return n;
        const g = graph.nodes.find((q) => q.id === n.id);
        if (!g) return n;
        const v = tick.nodeValues[n.id];
        const cue = cues[n.id];
        const cur = n.data as ActorNodeData;
        const patch = {
          label: g.label,
          icon: g.icon,
          group: g.group,
          hasAgent: !!g.agentConfig,
          hasWorkflowTrigger: workflowTriggerNodes?.has(g.id) ?? false,
          triggerDisarmed: workflowTriggerDisarmed?.has(g.id) ?? false,
          triggerFlash: workflowTriggerFlash?.has(g.id) ?? false,
          stock: v?.stock ?? 0,
          inflow: v?.inflow ?? 0,
          outflow: v?.outflow ?? 0,
          displayValue: g.displayValue,
          stateValue: g.displayValue?.startsWith('state:')
            ? v?.state?.[g.displayValue.slice('state:'.length)]
            : undefined,
          bottleneck: showOverlays && cue?.bottleneck === true,
          anomalous: showOverlays && cue?.anomalous === true,
          groupColor:
            showOverlays && cue?.clusterColor ? cue.clusterColor : g.group ? groupColor(g.group) : undefined,
        };
        for (const [k, val] of Object.entries(patch)) {
          if (cur[k as keyof ActorNodeData] !== val) {
            changed = true;
            return { ...n, data: { ...cur, ...patch } };
          }
        }
        return n;
      });
      return changed ? next : prev;
    });
  }, [graph.nodes, tick, cues, showOverlays, setRfNodes]);

  // Draw a new edge by dragging between handles. Creates a default goods edge
  // of weight 1; the user edits its kind/weight via the edge modal.
  const onConnectHandler = useCallback(
    (conn: Connection) => {
      if (!editable || !conn.source || !conn.target || !onConnect) return;
      // Omnidirectional graph (#6): allow MULTIPLE edges between the same pair
      // (so a back-link A→B and a forward B→A coexist as separate arrows).
      // The id is pair + random suffix; React Flow separates them visually as
      // parallel curves. Source/target handle ids are stored on the edge so a
      // connection drawn from the right side stays anchored on the right.
      const id = `${conn.source}__${conn.target}_${Math.random().toString(36).slice(2, 8)}`;
      const edge: GraphEdge = {
        id,
        source: conn.source,
        target: conn.target,
        sourceHandle: conn.sourceHandle ?? undefined,
        targetHandle: conn.targetHandle ?? undefined,
        flowKind: 'goods',
        weight: 1,
        params: [],
      };
      onConnect(edge);
    },
    [editable, onConnect],
  );

  // Re-anchor an edge endpoint: RF fires onReconnect with the new connection
  // after the user drags a reconnect handle to a different node/side. We pass
  // the diff up to App which calls adapter.updateEdgeHandles.
  const onReconnectHandler = useCallback(
    (oldEdge: Edge, conn: Connection) => {
      if (!editable || !onReconnectEdge) return;
      onReconnectEdge(oldEdge.id, {
        source: conn.source,
        target: conn.target,
        sourceHandle: conn.sourceHandle ?? undefined,
        targetHandle: conn.targetHandle ?? undefined,
      });
    },
    [editable, onReconnectEdge],
  );

  // Double-click on an edge → add a bend. The bend is placed at the point ON
  // the rendered curve nearest the click (not the raw cursor position — for a
  // bezier edge the cursor can be tens of px off the visible path, and dropping
  // a bend there would make the curve lurch to the wrong side). We also work
  // out which segment of the bend chain the click landed on so the new bend
  // is INSERTED between its real neighbours rather than appended at the end
  // (appending puts every new bend after all existing ones, which on a
  // multi-bend edge bends the wrong segment).
  const onEdgeDoubleClick = useCallback(
    (e: React.MouseEvent, edge: Edge) => {
      if (!editable || !onAddBend) return;

      const flowData = edge.data as FlowEdgeData;
      if (flowData.isAuto && onPromoteEdge) {
        onPromoteEdge(edge.id, flowData.computedSourceHandle, flowData.computedTargetHandle, flowData.bends ?? []);
      }

      // Sample the rendered path: find the (x, y) on the curve nearest the
      // click + the arc-length fraction `t` where it sits. The chain has
      // N+1 segments for N bends (src→b0→...→bN-1→tgt); map `t` to a segment
      // index so we insert the new bend in the right place.
      const pathEl = document.getElementById(edge.id) as SVGPathElement | null;
      const existing = flowData.bends ?? [];
      let bx: number;
      let by: number;
      let insertIdx = existing.length; // default: append at the end
      if (pathEl && typeof pathEl.getTotalLength === 'function') {
        const total = pathEl.getTotalLength();
        if (total > 0) {
          const fp = reactFlow.screenToFlowPosition({ x: e.clientX, y: e.clientY });
          const N = 60;
          let bestT = 0;
          let bestD = Infinity;
          let bestX = 0;
          let bestY = 0;
          for (let i = 0; i <= N; i++) {
            const pt = pathEl.getPointAtLength((i / N) * total);
            const dd = (pt.x - fp.x) ** 2 + (pt.y - fp.y) ** 2;
            if (dd < bestD) { bestD = dd; bestT = i / N; bestX = pt.x; bestY = pt.y; }
          }
          bx = bestX;
          by = bestY;
          // The bend chain splits the path into (existing.length + 1) equal-ish
          // arc-length spans. Pick the span containing bestT; the new bend goes
          // at the END of that span's index (i.e. after span `k` bends).
          const spans = existing.length + 1;
          insertIdx = Math.min(existing.length, Math.floor(bestT * spans));
        } else {
          const p = reactFlow.screenToFlowPosition({ x: e.clientX, y: e.clientY });
          bx = p.x; by = p.y;
        }
      } else {
        const p = reactFlow.screenToFlowPosition({ x: e.clientX, y: e.clientY });
        bx = p.x; by = p.y;
      }
      onAddBend(edge.id, bx, by, insertIdx, flowData.bends);
    },
    [editable, onAddBend, onPromoteEdge, reactFlow],
  );

  // "Add node" button: drops a new actor at the canvas centre. The user
  // customises it (name, icon, gate, params) via the NodeModal that opens
  // when they click the new node.
  // Clicking empty canvas clears selection. (App owns the context-menu state
  // and closes it on any selection change.)
  const onPaneClick = useCallback(() => {
    onSelectNode(null);
    onSelectEdge(null);
  }, [onSelectNode, onSelectEdge]);

  // Pane context menu: convert client coords to canvas coords for the
  // "Add node here" entry, then surface both up to App. The handler accepts
  // both native and React mouse events (RF's union type).
  const onPaneContextMenu = useCallback(
    (e: MouseEvent | React.MouseEvent) => {
      if (!editable) return; // read-only: no menu
      e.preventDefault();
      const client = { x: e.clientX, y: e.clientY };
      const canvas = reactFlow.screenToFlowPosition(client);
      onSelectNode(null);
      onSelectEdge(null);
      onContextPane?.(canvas, client);
    },
    [editable, reactFlow, onSelectNode, onSelectEdge, onContextPane],
  );

  return (
    <ReactFlow
      id="graph"
      nodes={rfNodes}
      edges={edges}
      nodeTypes={nodeTypes}
      edgeTypes={edgeTypes}
      onPaneClick={onPaneClick}
      onPaneContextMenu={onPaneContextMenu}
      onNodesChange={onNodesChange}
      onConnect={onConnectHandler}
      onEdgeDoubleClick={onEdgeDoubleClick}
      onReconnect={onReconnectHandler}
      // Keyboard deletes are handled by the App's global key layer (which
      // routes through the adapter ops so the edit is committed + undoable).
      // RF's built-in Backspace/delete would remove nodes from its VIEW store
      // only — no bundle write, no confirm, and the canvas desyncs from the
      // model until the next edit. Never re-enable the default key here.
      deleteKeyCode={null}
      // How close the cursor must be to an endpoint to grab it (px).
      reconnectRadius={10}
      // Loose mode: any handle connects to any handle, so the user can drag
      // from any side of a node to any side of another (and back-links work).
      connectionMode={ConnectionMode.Loose}
      zoomOnDoubleClick={zoomOnDoubleClick}
      nodesConnectable={editable}
      nodesDraggable={editable}
      fitView
      fitViewOptions={{ padding: 0.2 }}
      proOptions={{ hideAttribution: true }}
    >
      {/* Grid + groups toggles used to live here as ghost buttons in the
          top-right corner. They moved to the View menu in the MenuBar — the
          canvas is now free of toolbar chrome. */}
      <Background
        variant={showGrid ? BackgroundVariant.Lines : BackgroundVariant.Dots}
        gap={showGrid ? 24 : 20}
        size={2}
      />
      {/* Minimap — toggled from the StatusBar. The old hack that hid it while
          the sim ticked (visibility: hidden) is gone; the user owns the
          toggle now. */}
      {showMinimap && (
        <MiniMap
          pannable
          zoomable
          nodeColor={(n) => (n.data as ActorNodeData).groupColor ?? '#4c72b0'}
          maskColor="rgba(15, 17, 21, 0.7)"
        />
      )}
      <Controls showInteractive={false} />
    </ReactFlow>
  );
},
);

